import { useState, useEffect, useCallback } from 'react'
import {
  fetchSessionCharacters,
  fetchCharacterVTTData,
  getCachedCharacter,
  findCharacterByPlayerId,
  invalidateCharacter,
  VTTCharacterData,
} from '@/services/characterService'

interface UseCharacterDataOptions {
  sessionId: string
  token: string
  enabled?: boolean
}

interface UseCharacterDataResult {
  characters: VTTCharacterData[]
  loading: boolean
  error: string | null
  getCharacter: (characterId: string) => VTTCharacterData | null
  getCharacterByPlayerId: (playerId: string) => VTTCharacterData | null
  fetchCharacter: (characterId: string) => Promise<VTTCharacterData | null>
  refetch: () => Promise<void>
  /**
   * Refresh a single character — invalidates the cache and refetches,
   * then merges the fresh row into the hook's `characters` state so
   * subscribers re-render with the new data. Used by:
   *  - useWebSocket's onCharacterNeedsRefresh callback when a token
   *    spawns / re-links to a characterId we haven't loaded.
   *  - CharacterSheetModal on open so the GM can see edits made on the
   *    platform without a full reload.
   *  - NpcTokenModal after creating a new character so the sidebar
   *    populates without waiting for the next session-state replay.
   */
  refetchCharacter: (characterId: string, options?: { invalidate?: boolean }) => Promise<VTTCharacterData | null>
}

/**
 * Hook for managing character data in the VTT
 *
 * Fetches all session characters on mount and provides
 * methods to access individual character data.
 */
export function useCharacterData({
  sessionId,
  token,
  enabled = true,
}: UseCharacterDataOptions): UseCharacterDataResult {
  const [characters, setCharacters] = useState<VTTCharacterData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch all session characters
  const fetchAll = useCallback(async () => {
    if (!sessionId || !token || !enabled) return

    setLoading(true)
    setError(null)

    try {
      const data = await fetchSessionCharacters(sessionId, token)
      setCharacters(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch characters')
    } finally {
      setLoading(false)
    }
  }, [sessionId, token, enabled])

  // Fetch on mount
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Get character by ID. Prefers the in-state `characters` row, falling back
  // to the module cache for ids not in the array (e.g. a character fetched
  // individually via fetchCharacter that hasn't been merged into state).
  //
  // Depending on `characters` is deliberate: it gives this accessor a fresh
  // identity whenever session character data loads or a sheet is refetched.
  // Consumers that memoize on the accessor — notably VTTCanvas's token-draw
  // effect, which creates the HP pill only when the linked character's HP is
  // available — re-run and pick up the now-loaded data. Without this, a reload
  // drew tokens before the async character fetch resolved and never redrew, so
  // HP bars vanished until the next unrelated token mutation (VTT #136).
  const getCharacter = useCallback(
    (characterId: string): VTTCharacterData | null => {
      return (
        characters.find((c) => c.id === characterId) ??
        getCachedCharacter(characterId)
      )
    },
    [characters]
  )

  // Get character by player ID
  const getCharacterByPlayerId = useCallback(
    (playerId: string): VTTCharacterData | null => {
      return findCharacterByPlayerId(sessionId, playerId)
    },
    [sessionId]
  )

  // Fetch a specific character (if not in cache)
  const fetchCharacter = useCallback(
    async (characterId: string): Promise<VTTCharacterData | null> => {
      // Check cache first
      const cached = getCachedCharacter(characterId)
      if (cached) return cached

      // Fetch from API
      return fetchCharacterVTTData(characterId, token)
    },
    [token]
  )

  const refetchCharacter = useCallback(
    async (
      characterId: string,
      opts: { invalidate?: boolean } = { invalidate: true }
    ): Promise<VTTCharacterData | null> => {
      if (!token) return null
      if (opts.invalidate) invalidateCharacter(characterId)
      const fresh = await fetchCharacterVTTData(characterId, token, {
        bypassCache: true,
      })
      if (fresh) {
        setCharacters((prev) => {
          const idx = prev.findIndex((c) => c.id === fresh.id)
          if (idx === -1) return [...prev, fresh]
          const next = prev.slice()
          next[idx] = fresh
          return next
        })
      }
      return fresh
    },
    [token]
  )

  return {
    characters,
    loading,
    error,
    getCharacter,
    getCharacterByPlayerId,
    fetchCharacter,
    refetch: fetchAll,
    refetchCharacter,
  }
}
